import type { FirebaseApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { callableReadKey, runKeyedAdmittedCallableRead } from '@kop/callable-read-admission';
import {
  hqEmphasisAttachmentSchema,
  hqEmphasisEvidencePreparedSchema,
  hqEmphasisEvidenceOpenedSchema,
  hqEmphasisSaveInputSchema,
  hqEmphasisSaveResultSchema,
  hqEmphasisLoadInputSchema,
  hqEmphasisLoadResultSchema,
  hqEmphasisEvidencePrepareSchema,
  hqEmphasisEvidenceKeySchema,
  hqEmphasisEvidenceOpenSchema,
  type HqEmphasisSaveInput,
} from '@kop/contracts';

export function createHqEmphasisPortForApp(app: FirebaseApp, expectedUid: string) {
  const auth = getAuth(app),
    functions = getFunctions(app, 'asia-northeast3');
  const current = () => {
    if (!expectedUid || auth.currentUser?.uid !== expectedUid)
      throw Error('HQ_EMPHASIS_AUTH_SESSION_CHANGED');
  };
  const call = async (name: string, payload: unknown, read = false) => {
    await auth.authStateReady();
    current();
    const result = await httpsCallable(functions, name, {
      limitedUseAppCheckTokens: !read,
      timeout: 120000,
    })(payload);
    current();
    return result.data;
  };
  async function evidence(
    operation: 'prepare',
    input: Readonly<Record<string, unknown>>,
  ): Promise<ReturnType<typeof hqEmphasisEvidencePreparedSchema.parse>>;
  async function evidence(
    operation: 'commit',
    input: Readonly<Record<string, unknown>>,
  ): Promise<ReturnType<typeof hqEmphasisAttachmentSchema.parse>>;
  async function evidence(
    operation: 'open',
    input: Readonly<Record<string, unknown>>,
  ): Promise<ReturnType<typeof hqEmphasisEvidenceOpenedSchema.parse>>;
  async function evidence(
    operation: 'prepare' | 'commit' | 'open',
    raw: Readonly<Record<string, unknown>>,
  ) {
    const name = {
      prepare: 'prepareHqEmphasisEvidence',
      commit: 'commitHqEmphasisEvidence',
      open: 'openHqEmphasisEvidence',
    }[operation];
    const input =
      operation === 'prepare'
        ? hqEmphasisEvidencePrepareSchema.parse(raw)
        : operation === 'commit'
          ? hqEmphasisEvidenceKeySchema.parse(raw)
          : hqEmphasisEvidenceOpenSchema.parse(raw);
    const result =
      operation === 'open'
        ? await runKeyedAdmittedCallableRead(
            callableReadKey(expectedUid, name, input),
            () => call(name, input, true),
            undefined,
            'hq-media',
          )
        : await call(name, input);
    return operation === 'prepare'
      ? hqEmphasisEvidencePreparedSchema.parse(result)
      : operation === 'commit'
        ? hqEmphasisAttachmentSchema.parse(result)
        : hqEmphasisEvidenceOpenedSchema.parse(result);
  }
  return {
    async load(raw: Readonly<{ year: number; half: 1 | 2 }>) {
      const input = hqEmphasisLoadInputSchema.parse(raw);
      await auth.authStateReady();
      current();
      const result = await runKeyedAdmittedCallableRead(
        callableReadKey(expectedUid, 'loadHqEmphasis', input),
        () => call('loadHqEmphasis', input, true),
      );
      return hqEmphasisLoadResultSchema.parse(result);
    },
    async save(raw: HqEmphasisSaveInput) {
      const input = hqEmphasisSaveInputSchema.parse(raw);
      return hqEmphasisSaveResultSchema.parse(await call('saveHqEmphasis', input));
    },
    evidence,
  };
}
export type HqEmphasisPort = ReturnType<typeof createHqEmphasisPortForApp>;
